import React, { useEffect, useRef, useCallback, useState } from 'react'; // Import useState
import { Box, Button, Container, Typography, Stack, CircularProgress } from '@mui/material'; // Import CircularProgress

// IMPORTANT: Replace 'yourVideoFileName.mp4' with the actual name of your video file
// For example, if your video is '13476-248644895_medium.mp4', it would be:
// import scrapTradeVideo from '../../assets/13476-248644895_medium.mp4';
import scrapTradeVideo from '../../assets/home_video.mp4'; // Using the specific video from your last query

import scrapBannerImage from '../../assets/scrap_trade_banner_image.jpg';

import { Link } from "react-router-dom"; // 👈 needed for navigation
import { useSelector } from "react-redux"; // 👈 get auth state

const HeroSection = ({ inView, elementsRef, themeColors }) => {
  // Ref for the video element to control playback speed if needed
  const videoRef = useRef(null);
  // State to track if the video has loaded enough to play through
  const [videoLoaded, setVideoLoaded] = useState(false);

  // 👇 auth state from Redux
  const { token, role } = useSelector((state) => state.auth);
  const isLoggedIn = Boolean(token);
  const isBuyerLoggedIn = Boolean(token && role === "buyer");

  useEffect(() => {
    if (inView) {
      if (videoRef.current) {
        videoRef.current.playbackRate = 0.7; // Set video to play at 70% speed (slow motion)
        // Check if video is already loaded (e.g., from cache)
        if (videoRef.current.readyState >= 3) { // READY_STATE_HAVE_FUTURE_DATA or HAVE_ENOUGH_DATA
          setVideoLoaded(true);
        }
      }
    }
  }, [inView, elementsRef]);

  // Handler for when the video can play through without buffering
  const handleVideoCanPlayThrough = useCallback(() => {
    setVideoLoaded(true);
  }, []);

  // Use useCallback for ref assignments to ensure stability and proper updates
  const setRef = useCallback((index, el) => {
    if (elementsRef && elementsRef.current) { // Ensure elementsRef and elementsRef.current are not null/undefined
      elementsRef.current[index] = el;
    }
  }, [elementsRef]); // Dependency on elementsRef itself

  return (
    <Box
      sx={{
        position: 'relative',
        py: { xs: 10, md: 15 },
        minHeight: '80vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        backgroundImage: `
          linear-gradient(
            135deg,
            rgba(244, 197, 66, 0.65) 0%,
            rgba(230, 183, 58, 0.65) 40%,
            rgba(212, 162, 41, 0.65) 100%
          ),
          url(${scrapBannerImage})
        `,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }}
    >
      {/* Loader Box - displayed until video is loaded */}
      {/* {!videoLoaded && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'background.paper', // White background for loader
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10, // Higher zIndex to cover the video until it's ready
          }}
        >
          <CircularProgress sx={{ color: themeColors?.secondary || '#E6B73A' }} />
        </Box>
      )} */}

      {/* Video Background */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          overflow: 'hidden',
          zIndex: 1, // Place behind content but above the main background gradient
          // Hide video box until it's loaded to prevent flicker
          visibility: videoLoaded ? 'visible' : 'hidden',
          '& video': {
            objectFit: 'cover', // Cover the entire container
            width: '100%',
            height: '100%',
            filter: 'grayscale(70%) brightness(0.8)', // Apply grayscale and slightly dim
            opacity: 0.5, // Adjust opacity to blend with the gradient overlay
          },
        }}
      >
        {/* <video
          ref={videoRef}
          autoPlay
          loop
          muted
          playsInline // Important for autoplay on mobile
          preload="auto" // Preload video for smoother playback
          onCanPlayThrough={handleVideoCanPlayThrough} // Set videoLoaded to true when ready
        >

          <source src={scrapTradeVideo} type="video/mp4" />
          Your browser does not support the video tag.
        </video> */}
      </Box>

      <Container sx={{ textAlign: 'center', position: 'relative', zIndex: 2 }}>
        <Typography
          ref={(el) => setRef(0, el)}
          variant="h1"
          sx={{
            fontWeight: 800,
            mb: 2, // reduced bottom margin
            fontSize: { xs: "2.5rem", sm: "3.5rem", md: "4.5rem", lg: "5rem" },
            color: "#1a1a1a",
            letterSpacing: "-2px",
            textAlign: "center",
            lineHeight: 1.1, // keep lines tighter but readable
          }}
        >
          Verified Buyers
          <Box
            component="span"
            sx={{
              background: "linear-gradient(135deg, #fff 0%, rgba(255,255,255,0.8) 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              display: "block",
              my: 0.5, // small vertical margin (instead of mt+mb big gaps)
            }}
          >
            Trusted Sellers
          </Box>
          <Box
            component="span"
            sx={{
              color: themeColors?.secondary,
              display: "block",
              mt: 0.5, // small margin before last line
            }}
          >
            Secure Deals
          </Box>
        </Typography>


        <Typography
          ref={(el) => setRef(1, el)} // Use the useCallback ref
          variant="h5"
          sx={{
            mb: 8,
            fontSize: { xs: '1.2rem', sm: '1.4rem', md: '1.6rem' },
            color: 'rgba(255, 255, 255, 0.95)',
            fontWeight: 400,
            maxWidth: '600px',
            mx: 'auto',
            lineHeight: 1.6,
            textShadow: '0 1px 2px rgba(0,0,0,0.1)'
          }}
        >
          The leading and most trusted scrap trading platform: where verified buyers and sellers connect for secure, profitable, and sustainable deals.
        </Typography>

        <Stack
          ref={(el) => setRef(2, el)} // Use the useCallback ref
          direction={{ xs: 'column', sm: 'row' }}
          spacing={3}
          justifyContent="center"
          sx={{ mt: 6 }}
        >
          <Button
            component={Link}
            to={
              isLoggedIn
                ? role === "buyer"
                  ? "/dashboard"
                  : "/seller/dashboard" // Correct path for sellers
                : "/register"
            }
            variant="contained"
            size="large"
            sx={{
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(10px)',
              color: '#1a1a1a',
              borderRadius: 4,
              px: 6,
              py: 2,
              fontSize: '1.1rem',
              fontWeight: 600,
              textTransform: 'none',
              border: '1px solid rgba(255,255,255,0.3)',
              boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
              '&:hover': {
                background: 'rgba(255, 255, 255, 1)',
                transform: 'translateY(-3px)',
                boxShadow: '0 12px 40px rgba(0,0,0,0.15)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
              }
            }}
          >
            Start as Buyer →
          </Button>
          {!isBuyerLoggedIn && ( // 👈 hide button if buyer is logged in
            <Button
              component={Link}
              to={
                isLoggedIn
                  ? role === "seller"
                    ? "/seller/dashboard"
                    : "/seller/dashboard" // you can later change this if role-based dashboard differs
                  : "/register"
              }
              variant="contained"
              size="large"
              sx={{
                background: 'linear-gradient(135deg, #2D3A27 0%, #1a2118 100%)',
                color: '#fff',
                borderRadius: 4,
                px: 6,
                py: 2,
                fontSize: '1.1rem',
                fontWeight: 600,
                textTransform: 'none',
                boxShadow: '0 8px 32px rgba(45, 58, 39, 0.3)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #1a2118 0%, #0f1410 100%)',
                  transform: 'translateY(-3px)',
                  boxShadow: '0 12px 40px rgba(45, 58, 39, 0.4)',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                }
              }}
            >
              Join as Seller
            </Button>
          )}
        </Stack>

        {/* <Box sx={{ mt: 8, display: 'flex', justifyContent: 'center', gap: 6, opacity: 0.8 }}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#1a1a1a', mb: 1 }}>15K+</Typography>
            <Typography variant="body2" sx={{ color: 'rgba(26, 26, 26, 0.7)', fontWeight: 500 }}>Active Users</Typography>
          </Box>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#1a1a1a', mb: 1 }}>$2.5M+</Typography>
            <Typography variant="body2" sx={{ color: 'rgba(26, 26, 26, 0.7)', fontWeight: 500 }}>Monthly Volume</Typography>
          </Box>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#1a1a1a', mb: 1 }}>99.8%</Typography>
            <Typography variant="body2" sx={{ color: 'rgba(26, 26, 26, 0.7)', fontWeight: 500 }}>Success Rate</Typography>
          </Box>
        </Box> */}
      </Container>
    </Box>
  );
};

export default HeroSection;
